import { useCallback, useEffect, useMemo, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTIONS = { read: "Lettura", create: "Creazione", update: "Modifica", delete: "Eliminazione", export: "Esportazione",
  assign: "Assegnazione", manage: "Gestione", use: "Utilizzo", confidential: "Accesso" };
const COLS = Object.keys(ACTIONS);

const RoleList = ({ roles, selected, onSelect }) => (
  <Panel className="p-2 lg:col-span-3">
    {roles.map((r) => (
      <button key={r.key} onClick={() => onSelect(r.key)} data-testid={`role-item-${r.key}`}
        className={cn("flex w-full items-center justify-between rounded-control px-3 py-2.5 text-left text-sm transition-colors",
          selected === r.key ? "bg-brand-50 font-semibold text-ink" : "text-slate-600 hover:bg-slate-50")}>
        <span className="flex items-center gap-2">{r.locked && <Lock className="h-3 w-3" />}{r.name}</span>
        <span className="text-xs text-slate-400">{r.user_count}</span>
      </button>
    ))}
  </Panel>
);

const Matrix = ({ catalog, perms, editable, toggle }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[760px] text-sm" data-testid="admin-roles-permissions-matrix">
      <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-400">
        <th className="py-2 pr-4 font-medium">Modulo</th>
        {COLS.map((c) => <th key={c} className="px-2 py-2 text-center font-medium">{ACTIONS[c]}</th>)}
      </tr></thead>
      <tbody>
        {catalog.map((m) => (
          <tr key={m.module} className="border-b border-slate-50">
            <td className="py-2.5 pr-4"><p className="font-medium text-slate-800">{m.label}</p><p className="text-[11px] text-slate-400">{m.group}</p></td>
            {COLS.map((a) => (
              <td key={a} className="px-2 text-center">
                {m.actions.includes(a) && (
                  <Checkbox checked={perms.has(`${m.module}:${a}`)} disabled={!editable} onCheckedChange={() => toggle(`${m.module}:${a}`)}
                    data-testid={`perm-${m.module}-${a}`} />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminRoles() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState("super_admin");
  const [draft, setDraft] = useState({ perms: new Set(), scope: "own" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/roles").then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { load(); }, [load]);
  const role = useMemo(() => data?.roles.find((r) => r.key === selected), [data, selected]);
  useEffect(() => { if (role) setDraft({ perms: new Set(role.permissions), scope: role.data_scope }); }, [role]);

  const editable = can("roles:manage") && role && !role.locked;
  const toggle = (p) => setDraft((d) => { const s = new Set(d.perms); s.has(p) ? s.delete(p) : s.add(p); return { ...d, perms: s }; });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/roles/${selected}`, { permissions: [...draft.perms], data_scope: draft.scope });
      toast.success("Permessi aggiornati");
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-slate-500">Caricamento…</p>;
  return (
    <div data-testid="admin-roles-page">
      <PageHeader overline="Amministrazione" title="Ruoli e permessi"
        description="I permessi sono verificati dal server su ogni richiesta. L'ambito dati definisce quali record ogni ruolo può vedere." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <RoleList roles={data.roles} selected={selected} onSelect={setSelected} />
        <Panel className="p-5 lg:col-span-9">
          <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-ink" data-testid="role-detail-name">{role.name}</h2>
              <p className="mt-1 text-sm text-slate-500">{role.description}</p>
              {role.locked && <StatusBadge tone="neutral" className="mt-2">Ruolo di sistema bloccato: tutti i permessi</StatusBadge>}
            </div>
            <div className="flex items-center gap-2">
              <Select value={draft.scope} onValueChange={(v) => setDraft((d) => ({ ...d, scope: v }))} disabled={!editable}>
                <SelectTrigger className="h-9 w-56 text-sm" data-testid="role-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(data.data_scopes).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
              {editable && <Button onClick={save} disabled={busy} data-testid="role-save-button">Salva</Button>}
            </div>
          </div>
          <Matrix catalog={data.catalog} perms={draft.perms} editable={editable} toggle={toggle} />
        </Panel>
      </div>
    </div>
  );
}
